import {Card, closeDialog, DataStatus, openDialog} from 'platform/components';
import {Box, HStack, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback} from 'react';

import {head, isNil, mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {
  DmsDataGrid,
  catchUnhandledDataGridActions,
  logError,
  useWarehouseDataGridTreeFolder,
} from '@dms/shared';
import {LabourCatalogItemFrom} from '@dms/shared-aftersales';
import {LabourFolderTree} from '@dms/warehouse/shared-components';

import {TestIdProps, suffixTestId} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

interface LabourListProps extends TestIdProps {
  directSaleId: string;
  onAddItemToBasket: (labourCatalogId: string) => Promise<void>;
}

const PREDEFINED_COLUMNS = ['itemName'] as const;

export function RequestTabLabourList(props: LabourListProps) {
  const [folderTreeRef, dataGridModifier] = useWarehouseDataGridTreeFolder();

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
    refreshData,
  }) => {
    const labourCatalogId = isArray(rowId) ? head(rowId) : rowId;

    if (isNil(labourCatalogId)) {
      return logError('Row id is not defined');
    }

    match(actionKey)
      .with('addToBasket', () => props.onAddItemToBasket(labourCatalogId).then(() => refreshData()))
      .with('edit', () => {
        const LABOUR_CATALOG_ITEM_DIALOG = 'edit-catalog-item-dialog';

        const itemName = getColumnData<string>('itemName')?.value;

        openDialog(
          <LabourCatalogItemFrom
            editedId={labourCatalogId}
            onSubmitted={refreshData}
            onClose={() => closeDialog(LABOUR_CATALOG_ITEM_DIALOG)}
          />,
          {
            id: LABOUR_CATALOG_ITEM_DIALOG,
            title: itemName,
          }
        );
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => {
      const modifiedFilter = mergeAll([filter, {directSaleId: props.directSaleId}]);

      return dataGridModifier ? dataGridModifier?.queryModifier(modifiedFilter) : modifiedFilter;
    },
    [props.directSaleId, dataGridModifier]
  );

  return (
    <Card title={i18n.t('entity.warehouse.labels.itemsList')} isFullHeight>
      <HStack height="100%" spacing={4}>
        <Box
          flex={1}
          borderRight="1px solid"
          borderColor="palettes.neutral.40.100"
          paddingRight={4}
          height="100%"
          overflowY="auto"
        >
          <DataStatus>
            <VStack spacing={1} height="100%">
              <LabourFolderTree
                ref={folderTreeRef}
                data-testid={suffixTestId('articleFolderTree', props)}
              />
            </VStack>
          </DataStatus>
        </Box>

        <Box flex={3}>
          <DmsDataGrid
            // DG must be re-rendered on folder change to update the query modifier
            // eslint-disable-next-line no-restricted-syntax
            key={dataGridModifier?.key}
            gridCode="labour-catalog-ds"
            actionCallback={actionCallback}
            predefinedColumns={PREDEFINED_COLUMNS}
            queryModifier={queryModifier}
            data-testid={suffixTestId('datagrid', props)}
          />
        </Box>
      </HStack>
    </Card>
  );
}
